import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { leaveApi, leaveKeys, peopleApi, type PreviewInput } from "@/features/leave/api";
import type { QueryParams } from "@/lib/api/client";
import type { ChecklistItem, LeaveSettings } from "@/features/leave/types";
import { dashboardKeys } from "@/features/dashboard/hooks";

const CURRENT_YEAR = new Date().getFullYear();

export function useLeaveTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...leaveKeys.types(), includeInactive],
    queryFn: () => leaveApi.types(includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePublicHolidays(year?: number) {
  return useQuery({
    queryKey: leaveKeys.holidays(year),
    queryFn: () => leaveApi.holidays(year),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyBalances(year: number = CURRENT_YEAR) {
  return useQuery({
    queryKey: leaveKeys.myBalances(year),
    queryFn: () => leaveApi.myBalances(year),
  });
}

export function useMyLeaveRequests() {
  return useQuery({ queryKey: leaveKeys.myRequests(), queryFn: leaveApi.myRequests });
}

export function useLeaveQueue(filters: QueryParams) {
  return useQuery({
    queryKey: leaveKeys.queue(filters),
    queryFn: () => leaveApi.queue(filters),
  });
}

export function useBalancesFor(userId: string | undefined, year: number = CURRENT_YEAR) {
  return useQuery({
    queryKey: leaveKeys.balances(userId ?? "", year),
    queryFn: () => leaveApi.balancesFor(userId!, year),
    enabled: Boolean(userId),
  });
}

export function useBalanceEntries(userId: string | undefined, year: number = CURRENT_YEAR) {
  return useQuery({
    queryKey: leaveKeys.entries(userId ?? "", year),
    queryFn: () => leaveApi.entriesFor(userId!, year),
    enabled: Boolean(userId),
  });
}

/** The cost of a request, recalculated as the form changes. */
export function useLeavePreview(input: PreviewInput | null, asAdmin = false) {
  return useQuery({
    queryKey: [...leaveKeys.all, "preview", asAdmin, input],
    queryFn: () => (asAdmin ? leaveApi.adminPreview(input!) : leaveApi.preview(input!)),
    enabled: Boolean(input?.leave_type_id && input?.start_date && input?.end_date),
    // The answer depends on live balances, so never serve a stale one.
    staleTime: 0,
    retry: false,
  });
}

function useLeaveMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
      // The rail counts leave waiting for a decision.
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useRequestLeave() {
  return useLeaveMutation((payload: Record<string, unknown>) => leaveApi.request(payload));
}

export function useCancelMyLeave() {
  return useLeaveMutation((id: string) => leaveApi.cancelMine(id));
}

export function useDecideLeave() {
  return useLeaveMutation(
    ({
      id,
      approve,
      comment,
      overrideWarnings,
      coverFromOvertime,
    }: {
      id: string;
      approve: boolean;
      comment?: string;
      overrideWarnings?: boolean;
      /** HR's choice alone: spend banked overtime on the unpaid part. */
      coverFromOvertime?: boolean;
    }) =>
      approve
        ? leaveApi.approve(id, comment, overrideWarnings, coverFromOvertime)
        : leaveApi.reject(id, comment ?? "No reason given"),
  );
}

export function useCancelLeave() {
  return useLeaveMutation((id: string) => leaveApi.cancel(id));
}

export function useAdjustBalance() {
  return useLeaveMutation((payload: Record<string, unknown>) => leaveApi.adjustBalance(payload));
}

export function useRunAccrual() {
  return useLeaveMutation((year: number) => leaveApi.runAccrual(year));
}

export function useUpdateLeaveType() {
  return useLeaveMutation(({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
    leaveApi.updateType(id, payload),
  );
}

export function useCreateHoliday() {
  return useLeaveMutation((payload: Record<string, unknown>) => leaveApi.createHoliday(payload));
}

export function useUpdateHoliday() {
  return useLeaveMutation(({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
    leaveApi.updateHoliday(id, payload),
  );
}

export function useDeleteHoliday() {
  return useLeaveMutation((id: string) => leaveApi.deleteHoliday(id));
}

// ── Handover ─────────────────────────────────────────────────────────────────

export function useHandover(requestId: string | undefined) {
  return useQuery({
    queryKey: leaveKeys.handover(requestId ?? ""),
    queryFn: () => leaveApi.handover(requestId!),
    enabled: Boolean(requestId),
  });
}

/** Work somebody has left with me, and has not had back yet. */
export function useMyHandovers() {
  return useQuery({
    queryKey: leaveKeys.myHandovers(),
    queryFn: () => leaveApi.myHandovers(),
  });
}

export function useWriteHandover() {
  return useLeaveMutation(
    ({ requestId, ...payload }: { requestId: string; to_user_id: string; notes: string }) =>
      leaveApi.writeHandover(requestId, payload),
  );
}

export function useAcceptHandover() {
  return useLeaveMutation((requestId: string) => leaveApi.acceptHandover(requestId));
}

export function useHandBack() {
  return useLeaveMutation(({ requestId, notes }: { requestId: string; notes: string }) =>
    leaveApi.handBack(requestId, notes),
  );
}

export function useCloseHandover() {
  return useLeaveMutation((requestId: string) => leaveApi.closeHandover(requestId));
}

// ── Staying away longer ──────────────────────────────────────────────────────

/** What staying away until a later date would cost, as the date changes. */
export function useExtensionPreview(requestId: string | undefined, endDate: string | null) {
  return useQuery({
    queryKey: [...leaveKeys.all, "extension-preview", requestId, endDate],
    queryFn: () => leaveApi.previewExtension(requestId!, endDate!),
    enabled: Boolean(requestId && endDate),
    staleTime: 0,
    retry: false,
  });
}

export function useAskExtension() {
  return useLeaveMutation(
    ({ requestId, ...payload }: { requestId: string; end_date: string; reason: string | null }) =>
      leaveApi.askExtension(requestId, payload),
  );
}

export function useWithdrawExtension() {
  return useLeaveMutation((extensionId: string) => leaveApi.withdrawExtension(extensionId));
}

/** Extensions waiting for HR. */
export function useLeaveExtensions() {
  return useQuery({ queryKey: leaveKeys.extensions(), queryFn: () => leaveApi.extensions() });
}

export function useDecideExtension() {
  return useLeaveMutation(
    ({
      id,
      approve,
      comment,
      overrideWarnings,
    }: {
      id: string;
      approve: boolean;
      comment?: string;
      overrideWarnings?: boolean;
    }) =>
      approve
        ? leaveApi.approveExtension(id, comment, overrideWarnings)
        : leaveApi.rejectExtension(id, comment ?? "No reason given"),
  );
}

// ── Coming back ──────────────────────────────────────────────────────────────

export function useConfirmReturn() {
  return useLeaveMutation(
    ({
      requestId,
      ...payload
    }: {
      requestId: string;
      returned_on: string;
      note: string | null;
      record_gap_as_unpaid: boolean;
    }) => leaveApi.confirmReturn(requestId, payload),
  );
}

/** Whose account is waiting to be put back after leave. */
export function useReturning() {
  return useQuery({
    queryKey: leaveKeys.returning(),
    queryFn: () => leaveApi.returning(),
  });
}

export function useLeaveChecklist(requestId: string | undefined) {
  return useQuery({
    queryKey: leaveKeys.checklist(requestId ?? ""),
    queryFn: () => leaveApi.checklist(requestId!),
    enabled: Boolean(requestId),
  });
}

export function useSaveChecklist() {
  return useLeaveMutation(
    ({ requestId, items }: { requestId: string; items: ChecklistItem[] }) =>
      leaveApi.saveChecklist(requestId, items),
  );
}

/** Colleagues an employee may name, without needing the staff list. */
export function useColleagues(search?: string) {
  return useQuery({
    queryKey: ["colleagues", search ?? ""],
    queryFn: () => peopleApi.colleagues(search),
    staleTime: 5 * 60 * 1000,
  });
}

/** The leaving list, and which leave carries it. */
export function useLeaveSettings() {
  return useQuery({
    queryKey: leaveKeys.settings(),
    queryFn: () => leaveApi.settings(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveLeaveSettings() {
  return useLeaveMutation((payload: LeaveSettings) => leaveApi.saveSettings(payload));
}
